import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { environment } from '@env/environment';
import { BaseParamsService } from '../base-params.service';
import { LoggerService } from '@app/core/logger.service';

export interface EventPayload {
  event_type: string;
  page: string;
}

const mapHeadersToLogFormat = (headers: HttpHeaders): Record<string, string> =>
  headers.keys()
    .reduce((acc, key) => {
      const header = headers.get(key);
      if (header) {
        acc[key] = header;
      }
      return acc;
    }, {} as Record<string, string>);

@Injectable({
  providedIn: 'root',
})
export class DMPEventsSenderService {
  constructor(
    private http: HttpClient,
    private baseParamsService: BaseParamsService,
    private logger: LoggerService,
  ) { }

  public sendWebformEvent(payload: EventPayload): void {
    this.sendEvent(
      this.webformEventsApiUrl(),
      { event: payload },
    );
  }

  public sendOfferEvent(eventType: string, payload: any = {}): void {
    const url = `${this.offerEventsApiUrl()}/${eventType}`;
    this.sendEvent(url, { events: [payload] });
  }

  private sendEvent(url: string, params: any): void {
    if (!environment.dmpEvents) { return; }

    const headers = this.authenticationHeaders();
    if (!headers) { return; }

    if (this.currentSource()) { params.source = this.currentSource(); }
    if (this.currentSubsource()) { params.subsource = this.currentSubsource(); }

    this.http.post(url, params, { headers: headers })
      .subscribe({
        error: (error) => {
          if (error instanceof HttpErrorResponse && error.status === 422) {
            // Waiting for backend investigation https://placewisegroup.slack.com/archives/C013HJCGZ33/p1742447967458859
            this.logger.logError(new Error(`422 while sending dmp events`), {
              payload: params,
              response: error.error,
              // Info required by backend team
              // https://placewisegroup.slack.com/archives/C013HJCGZ33/p1745308331346729?thread_ts=1744950589.907239&cid=C013HJCGZ33
              headers: mapHeadersToLogFormat(headers),
            });
            return;
          }
          throw error;
        },
      });
  }

  private webformEventsApiUrl(): string {
    return `${environment.webforms_api}/v2/${this.baseParamsService.slug}/events`;
  }

  private offerEventsApiUrl(): string {
    return `${environment.webforms_api}/v3/${this.baseParamsService.slug}/members/me/events`;
  }

  private authenticationHeaders(): HttpHeaders | null {
    return this.baseParamsService.getAuthHeadersWithAutologin();
  }

  private currentSource(): string {
    return this.baseParamsService.source;
  }

  private currentSubsource(): string | null {
    return this.baseParamsService.subsource;
  }
}
